import { useEffect, useRef, useState } from "react";
import { QRCodeCanvas } from "qrcode.react";
import { jsPDF } from "jspdf";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Download } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

const LOGO_TURF_HOUSE = "https://customer-assets.emergentagent.com/job_field-rental/artifacts/nltdiuae_turf%20blog.png";
const LOGO_RIVERVIEW = "https://customer-assets.emergentagent.com/job_field-rental/artifacts/ov6p5t0e_Riverview%20Athletics%20Logo.png";

// Load an image and return its data URL — needed because jsPDF can't fetch
// cross-origin images itself.
function loadImageAsDataURL(src) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      const c = document.createElement("canvas");
      c.width = img.naturalWidth;
      c.height = img.naturalHeight;
      c.getContext("2d").drawImage(img, 0, 0);
      try {
        resolve({ dataUrl: c.toDataURL("image/png"), w: img.naturalWidth, h: img.naturalHeight });
      } catch (e) { reject(e); }
    };
    img.onerror = reject;
    img.src = src;
  });
}

export default function WaiverQRPosterPage() {
  const navigate = useNavigate();
  const qrRef = useRef(null);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    const prev = document.title;
    document.title = "Waiver QR — Print Poster";
    return () => { document.title = prev; };
  }, []);

  const url = `${window.location.origin}/waiver`;

  const downloadPDF = async () => {
    setDownloading(true);
    try {
      // 8.5 × 11 inches in points (1pt = 1/72in)
      const pdf = new jsPDF({ unit: "pt", format: "letter", orientation: "portrait" });
      const pageW = pdf.internal.pageSize.getWidth();   // 612
      const pageH = pdf.internal.pageSize.getHeight();  // 792

      // ── Logos centered at top ──
      const [thLogo, rvLogo] = await Promise.all([
        loadImageAsDataURL(LOGO_TURF_HOUSE),
        loadImageAsDataURL(LOGO_RIVERVIEW),
      ]);
      const logoH = 70;
      const thW = (thLogo.w / thLogo.h) * logoH;
      const rvW = (rvLogo.w / rvLogo.h) * logoH;
      const gap = 28;
      const totalW = thW + gap + rvW;
      let logoY = 60;
      let logoX = (pageW - totalW) / 2;
      pdf.addImage(thLogo.dataUrl, "PNG", logoX, logoY, thW, logoH);
      pdf.addImage(rvLogo.dataUrl, "PNG", logoX + thW + gap, logoY, rvW, logoH);

      // Divider
      pdf.setDrawColor(220);
      pdf.setLineWidth(0.7);
      pdf.line(72, logoY + logoH + 18, pageW - 72, logoY + logoH + 18);

      // ── Headline ──
      pdf.setTextColor(20);
      pdf.setFont("helvetica", "bold");
      pdf.setFontSize(44);
      pdf.text("Sign Your Waiver", pageW / 2, logoY + logoH + 70, { align: "center" });

      pdf.setFont("helvetica", "normal");
      pdf.setFontSize(16);
      pdf.setTextColor(95);
      pdf.text("Scan with your phone camera to get started.", pageW / 2, logoY + logoH + 98, { align: "center" });

      // ── QR code (pulled from on-screen canvas) ──
      const canvas = qrRef.current?.querySelector("canvas");
      if (!canvas) throw new Error("QR canvas not found");
      const qrDataUrl = canvas.toDataURL("image/png");
      const qrSize = 320;
      const qrX = (pageW - qrSize) / 2;
      const qrY = logoY + logoH + 130;
      // Black frame around QR
      pdf.setDrawColor(20);
      pdf.setLineWidth(3);
      const pad = 14;
      pdf.roundedRect(qrX - pad, qrY - pad, qrSize + pad * 2, qrSize + pad * 2, 10, 10);
      pdf.addImage(qrDataUrl, "PNG", qrX, qrY, qrSize, qrSize);

      // ── URL fallback ──
      const urlY = qrY + qrSize + 50;
      pdf.setFont("helvetica", "normal");
      pdf.setFontSize(10);
      pdf.setTextColor(140);
      pdf.text("OR VISIT", pageW / 2, urlY, { align: "center" });
      pdf.setFont("courier", "bold");
      pdf.setFontSize(16);
      pdf.setTextColor(20);
      pdf.text(url.replace(/^https?:\/\//, ""), pageW / 2, urlY + 22, { align: "center" });

      // ── Steps ──
      const stepsY = urlY + 70;
      pdf.setDrawColor(220);
      pdf.line(72, stepsY - 18, pageW - 72, stepsY - 18);
      const steps = [
        { n: "1", label: "Scan the QR code" },
        { n: "2", label: "Fill out the form" },
        { n: "3", label: "Sign and submit" },
      ];
      const stepW = (pageW - 144) / 3;
      steps.forEach((s, i) => {
        const sx = 72 + stepW * i;
        // Circle
        pdf.setFillColor(20);
        pdf.circle(sx + 18, stepsY + 4, 16, "F");
        pdf.setTextColor(255);
        pdf.setFont("helvetica", "bold");
        pdf.setFontSize(15);
        pdf.text(s.n, sx + 18, stepsY + 10, { align: "center" });
        // Label
        pdf.setTextColor(20);
        pdf.setFontSize(12);
        pdf.text(s.label, sx + 44, stepsY + 8);
      });

      // ── Footnote ──
      pdf.setFont("helvetica", "normal");
      pdf.setFontSize(9);
      pdf.setTextColor(170);
      pdf.text(
        "Required before participating in any activity at Turf House / Riverview Athletics.",
        pageW / 2,
        pageH - 56,
        { align: "center" }
      );

      pdf.save("waiver-qr-poster.pdf");
    } catch (e) {
      toast.error("PDF generation failed: " + (e?.message || "unknown"));
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white text-gray-900" data-testid="qr-poster-page">
      <div className="border-b border-gray-200 px-6 py-3 flex items-center justify-between bg-white sticky top-0 z-10">
        <Button variant="ghost" size="sm" onClick={() => navigate("/signed-waivers")} data-testid="qr-back-btn">
          <ArrowLeft size={14} /> Back to Signed Waivers
        </Button>
        <Button onClick={downloadPDF} disabled={downloading} data-testid="qr-download-pdf-btn">
          <Download size={14} /> {downloading ? "Generating..." : "Download PDF Poster"}
        </Button>
      </div>

      <div className="mx-auto px-12 py-10 flex flex-col items-center text-center" style={{ maxWidth: 820 }}>
        <div className="flex items-center justify-center gap-6 pb-6 border-b border-gray-200 w-full">
          <img src={LOGO_TURF_HOUSE} alt="Turf House" style={{ height: 96 }} />
          <img src={LOGO_RIVERVIEW} alt="Riverview Athletics" style={{ height: 96 }} />
        </div>

        <h1 className="font-bold tracking-tight mt-10" style={{ fontSize: 56, lineHeight: 1.05 }}>
          Sign Your Waiver
        </h1>
        <p className="text-gray-600 mt-3" style={{ fontSize: 22 }}>
          Scan with your phone camera to get started.
        </p>

        {/* Live QR — also used as the source canvas for the PDF generator. */}
        <div ref={qrRef} className="my-10 p-6 bg-white border-4 border-gray-900 rounded-2xl shadow-sm">
          <QRCodeCanvas
            value={url}
            size={420}
            level="H"
            includeMargin={false}
            data-testid="waiver-qr-preview"
          />
        </div>

        <div className="text-center">
          <p className="uppercase tracking-widest text-gray-500" style={{ fontSize: 14 }}>Or visit</p>
          <p className="font-mono font-bold mt-1" style={{ fontSize: 24 }}>
            {url.replace(/^https?:\/\//, "")}
          </p>
        </div>

        <div className="mt-10 pt-6 border-t border-gray-200 w-full grid grid-cols-3 gap-6 text-left">
          <Step n="1" label="Scan the QR code" />
          <Step n="2" label="Fill out the form" />
          <Step n="3" label="Sign and submit" />
        </div>

        <p className="mt-10 text-gray-400" style={{ fontSize: 12 }}>
          Required before participating in any activity at Turf House / Riverview Athletics.
        </p>
      </div>
    </div>
  );
}

function Step({ n, label }) {
  return (
    <div className="flex items-start gap-3">
      <div className="rounded-full bg-gray-900 text-white font-bold flex items-center justify-center flex-shrink-0" style={{ width: 44, height: 44, fontSize: 20 }}>
        {n}
      </div>
      <p className="font-semibold pt-2" style={{ fontSize: 16 }}>{label}</p>
    </div>
  );
}
